import { ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TRUST_BADGES } from "@/data/services";

const HERO_BG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=85";

export default function Hero({ onBookClick }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] grain overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Modern smart home interior at night"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-40 md:pt-48 pb-24">
        <div className="max-w-3xl">
          <p
            data-testid="hero-overline"
            className="text-xs uppercase tracking-[0.28em] text-[#D4AF37] font-semibold mb-6 reveal"
          >
            Metro Atlanta · Licensed &amp; Insured
          </p>

          <h1
            data-testid="hero-headline"
            className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.02] text-white reveal"
            style={{ animationDelay: "60ms" }}
          >
            Elite Home Services,
            <span className="block text-white/55">
              seamlessly <span className="italic text-[#D4AF37] not-italic-fallback">installed.</span>
            </span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-7 text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl reveal"
            style={{ animationDelay: "140ms" }}
          >
            Smart locks. Cameras. Thermostats. Surround sound. Booked in seconds — installed
            by certified technicians your neighbors already trust.
          </p>

          <div
            className="mt-10 flex flex-col sm:flex-row gap-3 reveal"
            style={{ animationDelay: "220ms" }}
          >
            <Button
              data-testid="hero-cta-book"
              onClick={onBookClick}
              size="lg"
              className="bg-[#D4AF37] text-black hover:bg-[#C5A017] font-semibold rounded-full h-12 px-7 gold-glow transition-all duration-300"
            >
              Book Your Service
              <ArrowRight className="ml-1.5" size={18} />
            </Button>
            <a href="#services" data-testid="hero-cta-explore">
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-white/25 text-white hover:bg-white/5 hover:border-white/60 rounded-full h-12 px-7 font-medium"
              >
                Explore Services
              </Button>
            </a>
          </div>

          {/* Stat row */}
          <div
            className="mt-14 grid grid-cols-3 gap-6 max-w-xl reveal"
            style={{ animationDelay: "300ms" }}
          >
            <Stat label="Installs Completed" value="2,400+" />
            <Stat label="Average Rating" value="4.9★" />
            <Stat label="Years in Atlanta" value="10+" />
          </div>
        </div>
      </div>

      {/* Trust strip at bottom */}
      <div className="relative z-10 border-t border-white/10 bg-[#0A0A0C]/60 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-5 flex flex-wrap items-center justify-center md:justify-between gap-4">
          {TRUST_BADGES.map((b) => (
            <div
              key={b}
              data-testid={`trust-badge-${b.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center gap-2 text-sm text-white/65"
            >
              <Star size={14} className="text-[#D4AF37]" fill="#D4AF37" />
              <span>{b}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-2xl md:text-3xl font-semibold text-white">{value}</div>
      <div className="text-xs text-white/50 uppercase tracking-[0.18em] mt-1">{label}</div>
    </div>
  );
}
